import {
  getFCMToken,
  requestNotificationPermission,
  subscribeToForegroundMessages,
} from "../config/firebase";
import { toast } from "react-toastify";
import axios from "axios";
import {
  getRouteForNotification,
  navigateToRoute,
} from "../utils/notificationRouting";
import secureStorage from "../utils/secureStorage";

class FirebaseMessagingService {
  constructor() {
    this.token = null;
    this.isListening = false;
    this.unsubscribe = null;
  }

  // Initialize the service
  async initialize() {
    try {
      // Request permission first
      const permissionGranted = await requestNotificationPermission();

      if (!permissionGranted) {
        return false;
      }

      // Get FCM token
      this.token = await getFCMToken();

      if (this.token) {
        // Store token in secureStorage
        secureStorage.setItem("fcmToken", this.token);
        // Set axios default header immediately
        try {
          axios.defaults.headers.common["FCM-Token"] = this.token;
        } catch (e) {
          // Could not set axios header
        }
        return true;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  }

  // Get stored or fresh token
  async getToken() {
    if (this.token) {
      return this.token;
    }

    // Try to get from secureStorage first
    const storedToken = secureStorage.getItem("fcmToken");
    if (storedToken) {
      this.token = storedToken;
      return storedToken;
    }

    // Get fresh token
    this.token = await getFCMToken();
    if (this.token) {
      secureStorage.setItem("fcmToken", this.token);
    }

    return this.token;
  }

  // Start listening for foreground messages
  startListening(onNotificationReceived) {
    if (this.isListening) {
      return;
    }

    this.isListening = true;

    const handler = (payload) => {

      const notificationTitle =
        payload.notification?.title || "New Notification";
      const notificationBody =
        payload.notification?.body || "You have a new message";
      const route = getRouteForNotification(payload);

      // Fallback toast when browser Notifications aren't shown
      try {
        if (
          typeof Notification === "undefined" ||
          Notification.permission !== "granted"
        ) {
          toast.info(`${notificationTitle}: ${notificationBody}`, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            onClick: () => navigateToRoute(route),
          });
        }
      } catch (e) {
        // ignore toast errors
      }

      // Call custom handler if provided (this may show a browser Notification and handle click routing)
      try {
        if (onNotificationReceived) {
          onNotificationReceived(payload);
        }
      } catch (e) {
      }
    };

    // Subscribe using Firebase onMessage which returns unsubscribe
    subscribeToForegroundMessages(handler)
      .then((unsub) => {
        this.unsubscribe = unsub;
      })
      .catch((error) => {
      });
  }

  // Stop listening for messages
  stopListening() {
    this.isListening = false;
    try {
      if (this.unsubscribe) {
        this.unsubscribe();
        this.unsubscribe = null;
      }
    } catch (e) {
      // ignore
    }
  }

  // Check if notifications are supported
  isNotificationSupported() {
    return "Notification" in window && "serviceWorker" in navigator;
  }

  // Get current notification permission status
  getNotificationPermission() {
    if (!("Notification" in window)) {
      return "not-supported";
    }
    return Notification.permission;
  }

  // Send token to your backend
  async sendTokenToServer(token = null) {
    const fcmToken = token || (await this.getToken());

    if (!fcmToken) {
      return false;
    }

    try {
      // You can call your existing API here to update the FCM token
      // For now, we'll just store it in secureStorage
      secureStorage.setItem("fcmToken", fcmToken);
      try {
        axios.defaults.headers.common["FCM-Token"] = fcmToken;
      } catch (e) {
        // ignore
      }
      return true;
    } catch (error) {
      return false;
    }
  }

  // Clear token (for logout)
  clearToken() {
    this.token = null;
    secureStorage.removeItem("fcmToken");
    try {
      delete axios.defaults.headers.common["FCM-Token"];
    } catch (e) {
      // ignore
    }
  }
}

// Create singleton instance
const firebaseMessagingService = new FirebaseMessagingService();

export default firebaseMessagingService;
